import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DocumentRequestFormStyles from './styled';
import Checkbox from '../Inputs/Checkbox';
import Input from '../Input';
import Button from '../Button';
import MessageSuccess from '../Icons/MessageSuccess';
import states from '../../../data/states';
import submitContactForm from '../../../services/submitContactForm';
import noop from '../../../util/noop';

const statesShort = states.reduce(
  (_states, { value }) => [..._states, value],
  []
);

const initialValues = {
  reason: 'documents',
  DocumentRequests: [],
  loanNumber: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  comments: '',
};

const validationSchema = {
  reason: Yup.string().matches('documents').required(),
  DocumentRequests: Yup.array().required(),
  loanNumber: Yup.number()
    .min(0)
    .max(9999999, 'Loan Number must be fewer than 7 digits.')
    .required('Loan Number is required.'),
  firstName: Yup.string()
    .max(40, 'First Name is too long.')
    .required('First Name is required.'),
  lastName: Yup.string()
    .max(50, 'Last Name is too long.')
    .required('Last Name is required.'),
  email: Yup.string()
    .email('You must enter a valid email.')
    .required('Email is required.'),
  phone: Yup.string().matches(
    /^(\+?( |-|\.)?\d{1,2}( |-|\.)?)?(\(?\d{3}\)?|\d{3})( |-|\.)?(\d{3}( |-|\.)?\d{4})+$/,
    'Phone must be formatted correctly ###-###-####.'
  ),
  address: Yup.string().max(80, 'Address is too long.'),
  city: Yup.string().max(50, 'City is too long.'),
  state: Yup.string()
    .matches(
      new RegExp(statesShort.join('|')),
      'State must be selected from the list.'
    )
    .required('State is required.'),
  zip: Yup.string().matches(
    /^\d{5}(-?\d{4})?$/,
    'Zip must be formatted either XXXXX or XXXXX-XXXX.'
  ),
  comments: Yup.string().max(3000, 'Comments are too long.'),
};

const DocumentRequestForm = ({
  onFeesClick = noop,
  onCheckboxClick = noop,
  className = '',
  onSubmit: parentSubmit,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const onSubmit = (_values, { setSubmitting, setStatus, resetForm }) => {
    setSubmitting(true);
    parentSubmit();
    const values = Object.keys(_values).reduce(
      (vals, key) => ({
        ...vals,
        [key]:
          key !== 'DocumentRequests'
            ? _values[key] || null
            : _values[key].map(document => ({
                RequestedDocument: document,
              })),
      }),
      {}
    );

    submitContactForm(values)
      .then(data => {
        setIsSubmitted(true);
        setSubmitting(false);
        resetForm(initialValues);
      })
      .catch(error => {
        setIsSubmitted(false);
        setSubmitting(false);
        setStatus(error.response.data);
      })
      .finally(() => {
        window.scrollTo(0, 200);
      });
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    touched,
    values,
    status,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit,
  });

  const handleSubmitClick = e => {
    e.stopPropagation();
    e.preventDefault();

    handleSubmit();
  };

  if (isSubmitted) {
    return (
      <DocumentRequestFormStyles>
        <section className='thank-you'>
          <MessageSuccess />
          <h2>Document Request</h2>
          <p className='type-h4'>Thank you! Your request has been submitted.</p>
          <Button onClick={() => setIsSubmitted(false)}>Okay</Button>
        </section>
      </DocumentRequestFormStyles>
    );
  }

  return (
    <DocumentRequestFormStyles>
      <form>
        <fieldset className='forms' disabled={isSubmitting}>
          <legend className='type-h2'>Document Request</legend>
          <p className='type-caption'>
            To help us respond quickly, please complete the information listed
            below. We will process your request within 24 to 48 hours during our
            normal business days, Monday-Friday.{' '}
            <strong>
              As a reminder, all documents you request will be mailed to the
              address listed on your loan.
            </strong>
          </p>
          <p className='type-caption'>
            There is no charge for any of the documents listed below. If you
            would like to request additional information, please see the
            complete list of{' '}
            <a
              href='/resources/faqs/my-vmf-account/maximum-fees-charged-for-available-services/'
              target='_blank'
              rel='noopener referrer'
              onClick={onFeesClick}
            >
              Vanderbilt’s Fees and Service Charges.
            </a>
          </p>
          <section className='options'>
            <Checkbox
              label='1098 Reprint* (specify year in comments)'
              value='1098 Reprint'
              name='DocumentRequests'
              onChange={handleChange}
              checked={values.DocumentRequests.indexOf('1098 Reprint') > -1}
              onClick={onCheckboxClick('1098_reprint')}
            />
            <Checkbox
              label='Amortization Schedule'
              value='Amortization Schedule'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('Amortization Schedule') > -1
              }
              onClick={onCheckboxClick('amortization_copy')}
            />
            <Checkbox
              label='0-12 Months Pay History'
              value='0-12 Months Pay History'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('0-12 Months Pay History') > -1
              }
              onClick={onCheckboxClick('12_months_doc')}
            />
            <Checkbox
              label='Copy of Contract'
              value='Copy of Contract'
              name='DocumentRequests'
              onChange={handleChange}
              checked={values.DocumentRequests.indexOf('Copy of Contract') > -1}
              onClick={onCheckboxClick('contract_copy')}
            />
            <Checkbox
              label='13-24 Months Pay History'
              value='13-24 Months Pay History'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('13-24 Months Pay History') > -1
              }
              onClick={onCheckboxClick('24_months_doc')}
            />
            <Checkbox
              label='Copy of Title'
              value='Copy of Title'
              name='DocumentRequests'
              onChange={handleChange}
              checked={values.DocumentRequests.indexOf('Copy of Title') > -1}
              onClick={onCheckboxClick('title_copy')}
            />
            <Checkbox
              label='25-36 Months Pay History'
              value='25-36 Months Pay History'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('25-36 Months Pay History') > -1
              }
              onClick={onCheckboxClick('36_months_doc')}
            />
            <Checkbox
              label='Copy of Deed of Trust'
              value='Copy of Deed of Trust'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('Copy of Deed of Trust') > -1
              }
              onClick={onCheckboxClick('deed_copy')}
            />
            <Checkbox
              label='37-48 Months Pay History'
              value='37-48 Months Pay History'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('37-48 Months Pay History') > -1
              }
              onClick={onCheckboxClick('48_months_doc')}
            />
            <Checkbox
              label='Payoff Quote Inquiry* (specify good through date in comments)'
              value='Payoff Quote Inquiry'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf('Payoff Quote Inquiry') > -1
              }
              onClick={onCheckboxClick('payoff_quote_copy')}
            />
            <Checkbox
              label='49 or More Months Pay History'
              value='49 or More Months Pay History'
              name='DocumentRequests'
              onChange={handleChange}
              checked={
                values.DocumentRequests.indexOf(
                  '49 or More Months Pay History'
                ) > -1
              }
              onClick={onCheckboxClick('all_months_doc')}
            />
          </section>
        </fieldset>
        {values.DocumentRequests.length > 0 && (
          <fieldset className='contact' disabled={isSubmitting}>
            <legend className='type-h4'>
              Contact Information – in case we have a question about your
              request
            </legend>
            <div className='row'>
              <Input
                type='text'
                formatter='number'
                onChange={handleChange}
                onBlur={handleBlur}
                name='loanNumber'
                value={values?.loanNumber}
                error={errors?.loanNumber}
                touched={touched?.loanNumber}
                label='Loan Number'
                note='*Required'
              />
            </div>
            <div className='row'>
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='firstName'
                value={values?.firstName}
                error={errors?.firstName}
                touched={touched?.firstName}
                label='First Name'
                note='*Required'
              />
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='lastName'
                value={values?.lastName}
                error={errors?.lastName}
                touched={touched?.lastName}
                label='Last Name'
                note='*Required'
              />
            </div>
            <div className='row'>
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='email'
                value={values?.email}
                error={errors?.email}
                touched={touched?.email}
                label='Email Address'
                note='*Required'
              />
            </div>
            <div className='row'>
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='address'
                value={values?.address}
                error={errors?.address}
                touched={touched?.address}
                label='Address'
              />
            </div>
            <div className='row'>
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='city'
                value={values?.city}
                error={errors?.city}
                touched={touched?.city}
                label='City'
              />
              <div className='row two-column'>
                <Input
                  type='dropdown'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name='state'
                  values={states}
                  value={values?.state}
                  error={errors?.state}
                  touched={touched?.state}
                  label='State'
                  className='mobile-half'
                  note='*Required'
                  showFormatted={false}
                />
                <Input
                  type='text'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name='zip'
                  value={values?.zip}
                  error={errors?.zip}
                  touched={touched?.zip}
                  label='Zip Code'
                  className='mobile-half'
                />
              </div>
            </div>
            <div className='row'>
              <Input
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                name='phone'
                value={values?.phone}
                error={errors?.phone}
                touched={touched?.phone}
                label='Home Phone'
                formatter='phone'
              />
            </div>
            <div className='row'>
              <Input
                type='textarea'
                onChange={handleChange}
                onBlur={handleBlur}
                name='comments'
                value={values?.comments}
                error={errors?.comments}
                touched={touched?.comments}
                label='Comments'
              />
            </div>
            <div className='row'>
              <Button
                onClick={handleSubmitClick}
                appearance='dark'
                type='submit'
                size='large'
                className={className}
              >
                Submit
              </Button>
            </div>
          </fieldset>
        )}
      </form>
    </DocumentRequestFormStyles>
  );
};

DocumentRequestForm.propTypes = {};

export default DocumentRequestForm;
